import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { ActivityInsightFlag } from '../types/usage-insights.types';
import {
  formatEvidenceValue,
  formatFlagCodeHeadline,
  formatMetricValue,
  getSeverityBadgeClass,
  getSeverityLabel,
} from '../utils/usage-insights.utils';

interface UsageInsightsFlagsSectionProps {
  flags: ActivityInsightFlag[];
}

export function UsageInsightsFlagsSection({ flags }: UsageInsightsFlagsSectionProps) {
  if (flags.length === 0) {
    return (
      <section className="rounded-xl border border-[#EDEEF2] bg-white p-5">
        <h2 className={portalDs.sectionTitle}>Flags</h2>
        <p className={cn(portalDs.caption, 'mt-2')}>No flags for the selected period.</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-[#EDEEF2] bg-white p-5">
      <h2 className={portalDs.sectionTitle}>Flags</h2>
      <div className="mt-4 space-y-3">
        {flags.map((flag) => (
          <FlagRow key={flag.code} flag={flag} />
        ))}
      </div>
    </section>
  );
}

function FlagRow({ flag }: { flag: ActivityInsightFlag }) {
  const [expanded, setExpanded] = useState(false);
  const evidenceEntries = Object.entries(flag.evidence ?? {});

  return (
    <div className="rounded-lg border border-[#EDEEF2] bg-[#FAFAFB]">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                getSeverityBadgeClass(flag.severity),
              )}
            >
              {getSeverityLabel(flag.severity)}
            </span>
            <h3 className="text-sm font-semibold text-[#0D0F1A]">
              {formatFlagCodeHeadline(flag.code)}
            </h3>
          </div>
          <p className={cn(portalDs.caption, 'mt-1')}>
            {formatMetricValue(flag.value)}
            {' '}
            vs baseline
            {' '}
            {formatMetricValue(flag.baseline)}
          </p>
        </div>
        <ChevronDown
          className={cn('mt-1 size-4 shrink-0 text-[#5E6278] transition-transform', expanded && 'rotate-180')}
        />
      </button>

      {expanded ? (
        <div className="border-t border-[#EDEEF2] px-4 py-3">
          <p className={cn(portalDs.caption, 'mb-2 uppercase tracking-[0.5px]')}>Evidence</p>
          {evidenceEntries.length === 0 ? (
            <p className="text-sm text-[#5E6278]">No evidence payload provided.</p>
          ) : (
            <dl className="grid gap-2 sm:grid-cols-2">
              {evidenceEntries.map(([key, value]) => (
                <div key={key} className="rounded-md border border-[#EDEEF2] bg-white px-3 py-2">
                  <dt className={cn(portalDs.caption, 'uppercase tracking-[0.5px]')}>
                    {formatFlagCodeHeadline(key)}
                  </dt>
                  <dd className="mt-1 whitespace-pre-wrap text-sm font-medium text-[#0D0F1A]">
                    {formatEvidenceValue(value)}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ) : null}
    </div>
  );
}
